import * as React from 'react';
import { Input } from '@/components/ui/input';
import { cn, sanitizeDecimalInput, parseDecimalInput } from '@/lib/utils';

type InputProps = React.ComponentProps<typeof Input>;

export interface DecimalInputProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'inputMode'> {
  value: number | null;
  onValueChange: (value: number | null) => void;
}

// Locale-independent decimal input. Accepts "." or "," as decimal separator
// and preserves whichever the user typed. Numeric value is exposed to the
// parent via onValueChange; the internal string buffer is private.
//
// Manual QA checklist (run before changing this file or the helpers in lib/utils.ts):
//   1. Type "3,2"  → input shows "3,2", parent receives 3.2.
//   2. Type "3.2"  → input shows "3.2", parent receives 3.2.
//   3. Type "3,"   → input shows "3,",  parent receives null (incomplete).
//   4. Type "3"    → parent receives 3.
//   5. Type "3,2,5" or "3.2.5" → second separator is ignored (only first sticks).
//   6. Paste "abc4,7xyz" → input shows "4,7", parent receives 4.7.
//   7. Reload a saved draft with tsh=3.2 → input renders as "3.2".
//   8. Delete to empty → parent receives null.
export const DecimalInput = React.forwardRef<HTMLInputElement, DecimalInputProps>(
  ({ value, onValueChange, className, ...rest }, ref) => {
    const [buffer, setBuffer] = React.useState<string>(
      value != null ? String(value) : ''
    );

    // Re-sync the buffer when value changes externally (e.g., draft load,
    // form reset). Skip if the buffer already parses to value, so we never
    // overwrite a partially-typed string like "3," or "3.".
    React.useEffect(() => {
      if (parseDecimalInput(buffer) !== value) {
        setBuffer(value != null ? String(value) : '');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={buffer}
        onChange={(e) => {
          const cleaned = sanitizeDecimalInput(e.target.value);
          setBuffer(cleaned);
          onValueChange(parseDecimalInput(cleaned));
        }}
        className={cn('font-mono tabular-nums', className)}
        {...rest}
      />
    );
  }
);
DecimalInput.displayName = 'DecimalInput';
